import React, { useCallback, useEffect, useState, useDeferredValue } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from "recharts";
import { SensorDataFile, SensorDataFileRow } from "@shared/models";
import { SensorDataFileValueFilter } from "@shared/interfaces";
import { Loader } from "@shared/components";
import { CategoricalChartState } from "recharts/types/chart/generateCategoricalChart";
import { getChartPrevNextTime } from "@shared/utils";
import { AUTOPLAY_INTERVAL } from "@shared/constants";

import { CustomTooltip } from "./components/CustomTooltip";

import "./index.scss";

export interface SensorDataFileChartProps {
  file: SensorDataFile | null;
  items: SensorDataFileRow[];
  filter: SensorDataFileValueFilter | null;
  onChangeFilter: (filter: Partial<SensorDataFileValueFilter>) => void;
  loading: boolean;
}

const SensorDataFileChart: React.FC<SensorDataFileChartProps> = (props) => {
  const { items, filter, onChangeFilter, file, loading } = props;

  const deferredItems = useDeferredValue(items);

  const [isFirstIteration, setIsFirstIteration] = useState(true);
  const [nextTime, setNextTime] = useState<{ start?: string; end?: string }>({ start: undefined, end: undefined });
  const [prevTime, setPrevTime] = useState<{ start?: string; end?: string }>({ start: undefined, end: undefined });

  const [refAreaLeft, setRefAreaLeft] = useState<SensorDataFileRow | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<SensorDataFileRow | null>(null);
  const [isAutoNextPage, setIsAutoNextPage] = useState(true);

  useEffect(() => {
    if (filter && file && filter.start && filter.end && filter.interval) {
      const { next, prev } = getChartPrevNextTime(filter, filter.start, filter.end);
      setNextTime(next);
      setPrevTime(prev);
    }
  }, [filter, file]);

  const chartMouseDown = useCallback((item: CategoricalChartState) => {
    if (item?.activePayload?.length) {
      const activePayload = item.activePayload.find(Boolean);
      setRefAreaLeft(activePayload.payload);
    }
  }, []);

  const chartMouseMove = useCallback(
    (item: CategoricalChartState) => {
      if (item.activeLabel && refAreaLeft && item?.activePayload?.length) {
        const activePayload = item.activePayload.find(Boolean);
        setRefAreaRight(activePayload.payload);
      }
    },
    [refAreaLeft],
  );

  const chartMouseUp = useCallback(() => {
    setRefAreaRight(null);
    setRefAreaLeft(null);
  }, []);

  const getPrevItems = useCallback(() => {
    setIsFirstIteration(false);
    onChangeFilter(prevTime);
  }, [onChangeFilter, prevTime]);

  const getNextItems = useCallback(() => {
    if (nextTime.start) {
      setIsFirstIteration(false);
      onChangeFilter(nextTime);
    }
  }, [onChangeFilter, nextTime]);

  const handlePauseResumeStep = useCallback(() => {
    if (!isAutoNextPage) {
      getNextItems();
    }

    setIsAutoNextPage(!isAutoNextPage);
  }, [isAutoNextPage, getNextItems]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (isAutoNextPage && !loading) {
      interval = setTimeout(() => {
        getNextItems();
      }, AUTOPLAY_INTERVAL);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isAutoNextPage, getNextItems, items, loading]);

  return (
    <div className="sensor-data-file-chart-container">
      <div className="pagination-wrapper">
        <div className="auto-next" onClick={handlePauseResumeStep}>
          {isAutoNextPage ? "Pause" : "Resume"}
        </div>
        <button
          disabled={!prevTime.end || isFirstIteration || filter?.start === file?.start_time}
          onClick={getPrevItems}
        >
          <i className="fa fa-arrow-left" aria-hidden="true" />
        </button>
        <button disabled={!nextTime.start} onClick={getNextItems}>
          <i className="fa fa-arrow-right" aria-hidden="true" />
        </button>
      </div>
      {loading ? <Loader className="chart-loader" /> : null}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          onMouseDown={chartMouseDown}
          onMouseMove={chartMouseMove}
          onMouseUp={chartMouseUp}
          width={500}
          height={300}
          data={deferredItems}
        >
          <CartesianGrid stroke="#E3EBF0" />
          <XAxis dataKey="time" stroke="#869CAC" />
          <YAxis yAxisId="1" dataKey="value" />
          <Tooltip content={<CustomTooltip refAreaLeft={refAreaLeft} refAreaRight={refAreaRight} />} />
          <Line
            yAxisId="1"
            type="monotone"
            dataKey="value"
            stroke="#82ca9d"
            activeDot={{
              className: "active-dot",
              r: 5,
            }}
            dot={{
              r: 0,
            }}
          />
          {refAreaLeft && refAreaRight ? (
            <ReferenceArea yAxisId="1" x1={refAreaLeft.time} x2={refAreaRight.time} strokeOpacity={0.3} />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SensorDataFileChart;
